import { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache — one fetch per page load (mirrors useFeatureFlags).
let _cache = null;
let _inflight = null;
const _subs = new Set();

async function fetchOnce() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/features`, { credentials: 'include' });
      const data = res.ok ? await res.json() : null;
      _cache = data
        ? { loaded: true, subscription: data.subscription, bypass: !!data.bypass, features: new Set(data.features || []), catalog: data.catalog || {} }
        : { loaded: true, subscription: 'essential', bypass: false, features: new Set(), catalog: {} };
      _subs.forEach((fn) => fn(_cache));
      return _cache;
    } catch (_e) {
      _cache = { loaded: true, subscription: 'essential', bypass: false, features: new Set(), catalog: {} };
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * useSubscription — workshop tier gating (iter239).
 * Returns { loaded, subscription, hasFeature(key), catalog }.
 * hasFeature is OPTIMISTIC while loading (true) so essential nav never
 * flashes away; FeatureGate waits for `loaded` before blocking a route.
 */
export function useSubscription() {
  const [state, setState] = useState(_cache || { loaded: false, subscription: null, bypass: false, features: new Set(), catalog: {} });

  useEffect(() => {
    let cancelled = false;
    const handler = (s) => { if (!cancelled) setState(s); };
    _subs.add(handler);
    fetchOnce().then((s) => { if (!cancelled) setState(s); });
    return () => { cancelled = true; _subs.delete(handler); };
  }, []);

  return {
    loaded: state.loaded,
    subscription: state.subscription,
    catalog: state.catalog,
    hasFeature: (key) => {
      if (!state.loaded) return true;
      if (state.bypass) return true;
      return state.features.has(key);
    },
  };
}

/** Clear the cached tier on auth changes (login/logout) so a new user
 *  never inherits the previous session's feature set. Consumers refetch
 *  on next mount. */
export function invalidateSubscription() {
  _cache = null;
  _inflight = null;
}
